import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "../../components/admin/MediaPicker";
import RichEditor from "../../components/admin/RichEditor";

// Curated list of icons commonly useful for a plumbing/HVAC business.
const ICON_OPTIONS = [
  "Wrench", "Droplets", "Wind", "Snowflake", "Flame", "Thermometer", "ShowerHead",
  "Bath", "PaintBucket", "Hammer", "Cog", "Zap", "Settings", "Gauge", "Lightbulb",
  "Leaf", "Sun", "Home", "Building2", "Factory", "HardHat", "ShieldCheck",
  "Clock", "Phone", "Truck", "Package", "FileText", "Calendar", "Layers", "Star",
];

function IconRender({ name, className = "w-5 h-5" }) {
  const Cmp = LucideIcons[name] || LucideIcons.Wrench;
  return <Cmp className={className} />;
}

// ─────────────────────────── Icon picker (grid + upload) ───────────────────────────
function IconPicker({ value, customUrl, onPick, onUpload }) {
  const [tab, setTab] = useState(customUrl ? "upload" : "library");
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Max 512 Ko"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "icons");
    setUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(data.url);
      toast.success("Icône uploadée");
    } catch { toast.error("Upload impossible"); } finally { setUploading(false); }
  };

  return (
    <div className="border border-[hsl(var(--border))] rounded-xl p-3">
      <div className="flex items-center gap-1 mb-3 p-1 bg-[hsl(var(--muted))] rounded-full w-fit">
        <button type="button" onClick={() => setTab("library")} className={`px-3 h-7 rounded-full text-[11px] font-bold ${tab === "library" ? "bg-white shadow" : "text-[hsl(var(--muted-foreground))]"}`}>
          Bibliothèque
        </button>
        <button type="button" onClick={() => setTab("upload")} className={`px-3 h-7 rounded-full text-[11px] font-bold ${tab === "upload" ? "bg-white shadow" : "text-[hsl(var(--muted-foreground))]"}`}>
          Icône personnalisée
        </button>
      </div>
      {tab === "library" ? (
        <div className="grid grid-cols-8 sm:grid-cols-10 gap-1.5 max-h-48 overflow-y-auto">
          {ICON_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onPick(n)}
              title={n}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${value === n && !customUrl ? "bg-[color:var(--brand-sky)] text-white ring-2 ring-[color:var(--brand-sky)]/30" : "bg-[hsl(var(--muted))]/50 text-[color:var(--brand-navy)] hover:bg-[color:var(--brand-sky)]/15"}`}
              data-testid={`icon-opt-${n}`}
            >
              <IconRender name={n} className="w-5 h-5" />
            </button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {customUrl ? (
            <div className="w-14 h-14 rounded-xl bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] flex items-center justify-center overflow-hidden">
              <img src={customUrl} alt="" className="w-12 h-12 object-contain" />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-xl bg-[hsl(var(--muted))]/60 border-2 border-dashed border-[hsl(var(--border))] flex items-center justify-center text-[10px] text-[hsl(var(--muted-foreground))]">
              64×64
            </div>
          )}
          <div className="flex-1">
            <label className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-[hsl(var(--border))] bg-white text-xs font-semibold cursor-pointer hover:bg-[hsl(var(--muted))]/30">
              {uploading ? <LucideIcons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LucideIcons.Upload className="w-3.5 h-3.5" />}
              {customUrl ? "Remplacer" : "Uploader une icône"}
              <input type="file" accept="image/png,image/svg+xml,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="icon-file-input" />
            </label>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">PNG, SVG ou WEBP · 64×64 px recommandé · max 512 Ko</p>
            {customUrl && (
              <button type="button" onClick={() => onUpload("")} className="mt-1.5 text-[11px] text-red-600 hover:text-red-700 font-semibold inline-flex items-center gap-1">
                <LucideIcons.X className="w-3 h-3" /> Retirer l'icône personnalisée
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Categories manager ───────────────────────────
function CategoriesManager({ categories, onReload }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", icon: "Wrench", color: "sky", sort_order: 0 });

  const openNew = () => { setEdit(null); setForm({ name: "", slug: "", icon: "Wrench", color: "sky", sort_order: 0 }); setOpen(true); };
  const openEdit = (c) => { setEdit(c); setForm({ name: c.name, slug: c.slug, icon: c.icon || "Wrench", color: c.color || "sky", sort_order: c.sort_order || 0 }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom obligatoire");
    try {
      if (edit) await api.put(`/service-categories/${edit.id}`, form);
      else await api.post("/service-categories", form);
      toast.success(edit ? "Catégorie mise à jour" : "Catégorie créée");
      setOpen(false);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ? Les services liés conserveront leur contenu.`)) return;
    await api.delete(`/service-categories/${c.id}`);
    toast.success("Catégorie supprimée");
    onReload();
  };

  return (
    <Card className="p-5 border mb-6" data-testid="categories-manager">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-display font-bold text-[color:var(--brand-navy)]">Catégories de services</h2>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Pour classer et filtrer vos services sur le site.</p>
        </div>
        <Button size="sm" onClick={openNew} className="bg-[color:var(--brand-sky)]" data-testid="add-category-btn">
          <LucideIcons.Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle catégorie
        </Button>
      </div>
      {categories.length === 0 ? (
        <div className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))] italic">Aucune catégorie. Créez-en une pour classer vos services.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <div key={c.id} className="group inline-flex items-center gap-2 px-3 h-9 rounded-full bg-[hsl(var(--muted))]/50 border border-[hsl(var(--border))] text-xs font-semibold" data-testid={`cat-chip-${c.slug}`}>
              <IconRender name={c.icon || "Wrench"} className="w-3.5 h-3.5 text-[color:var(--brand-navy)]" />
              <span>{c.name}</span>
              <span className="text-[9px] text-[hsl(var(--muted-foreground))] font-mono">{c.slug}</span>
              <button onClick={() => openEdit(c)} className="opacity-0 group-hover:opacity-100 hover:text-[color:var(--brand-sky)]"><LucideIcons.Pencil className="w-3 h-3" /></button>
              <button onClick={() => del(c)} className="opacity-0 group-hover:opacity-100 hover:text-red-600"><LucideIcons.X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">
              {edit ? `Modifier « ${edit.name} »` : "Nouvelle catégorie"}
            </h3>
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: edit ? form.slug : "" })} /></div>
            <div><Label>Slug (optionnel — auto-généré)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="climatisation" /></div>
            <div>
              <Label>Icône</Label>
              <div className="grid grid-cols-8 gap-1.5 mt-1.5 max-h-36 overflow-y-auto p-2 border border-[hsl(var(--border))] rounded-lg">
                {ICON_OPTIONS.map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, icon: n })}
                    className={`w-9 h-9 rounded flex items-center justify-center ${form.icon === n ? "bg-[color:var(--brand-sky)] text-white" : "hover:bg-[hsl(var(--muted))]"}`}>
                    <IconRender name={n} className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Couleur</Label>
              <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["sky", "blue", "emerald", "amber", "red", "indigo", "purple", "slate"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Ordre d'affichage</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Annuler</Button>
              <Button onClick={save} className="flex-1 bg-[color:var(--brand-navy)]" data-testid="save-category-btn">{edit ? "Enregistrer" : "Créer"}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────── Main ServicesAdmin ───────────────────────────
export default function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showMedia, setShowMedia] = useState(false);

  const load = async () => {
    const [{ data }, { data: cats }] = await Promise.all([
      api.get("/services"), api.get("/service-categories"),
    ]);
    setItems(data);
    setCategories(cats);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({
    title: "", short_description: "", long_description: "", category: "", category_id: null,
    image_url: "", youtube_url: "", media_type: "image", featured: false, icon: "Wrench",
    icon_url: "", meta_title: "", meta_description: "", slug: "", keyword: "", sort_order: items.length,
  });

  const save = async () => {
    if (!edit.title) return toast.error("Titre requis");
    try {
      if (edit.id) await api.put(`/services/${edit.id}`, edit);
      else await api.post("/services", edit);
      toast.success("Service enregistré");
      setEdit(null);
      load();
    } catch { toast.error("Échec de l'enregistrement"); }
  };

  const del = async (s) => {
    if (!window.confirm(`Supprimer « ${s.title} » ?`)) return;
    await api.delete(`/services/${s.id}`);
    toast.success("Service supprimé");
    load();
  };

  const categoriesById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);

  return (
    <div className="p-6 md:p-10" data-testid="admin-services-page">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Services</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Gérez les prestations affichées sur votre site.</p>
        </div>
        <Button onClick={openNew} className="bg-[color:var(--brand-red)]" data-testid="add-service-btn">
          <LucideIcons.Plus className="w-4 h-4 mr-1.5" />Nouveau service
        </Button>
      </header>

      <CategoriesManager categories={categories} onReload={load} />

      {/* Services list */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => {
          const cat = s.category_id && categoriesById[s.category_id];
          return (
            <Card key={s.id} className="p-4 border flex gap-3 hover:shadow-md transition" data-testid={`service-card-${s.slug}`}>
              <div className="w-14 h-14 rounded-xl bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {s.icon_url ? <img src={s.icon_url} alt="" className="w-10 h-10 object-contain" /> : <IconRender name={s.icon || "Wrench"} className="w-6 h-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  {cat && (
                    <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-[color:var(--brand-navy)]/5 text-[color:var(--brand-navy)] text-[9px] font-black uppercase">
                      <IconRender name={cat.icon} className="w-2.5 h-2.5" /> {cat.name}
                    </span>
                  )}
                  {s.featured && <span className="px-1.5 h-5 rounded-full bg-amber-500 text-white text-[9px] font-bold inline-flex items-center">★</span>}
                </div>
                <div className="font-display font-bold text-[color:var(--brand-navy)] truncate">{s.title}</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{s.short_description}</p>
                <div className="mt-2 flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEdit(s)} className="text-xs h-7"><LucideIcons.Pencil className="w-3 h-3 mr-1" />Éditer</Button>
                  <Button size="sm" variant="outline" onClick={() => del(s)} className="text-xs h-7 text-red-600 hover:bg-red-50 border-red-200"><LucideIcons.Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Editor modal */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto py-8" onClick={() => setEdit(null)}>
          <Card className="max-w-3xl mx-auto p-6 md:p-8 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">{edit.id ? "Modifier" : "Nouveau"} — service</h2>
              <Button variant="ghost" onClick={() => setEdit(null)}><LucideIcons.X className="w-4 h-4" /></Button>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Titre *</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="service-title" /></div>
              <div><Label>Slug</Label><Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} placeholder="auto si vide" /></div>
              <div>
                <Label>Catégorie</Label>
                <Select value={edit.category_id || "__none"} onValueChange={(v) => setEdit({ ...edit, category_id: v === "__none" ? null : v })}>
                  <SelectTrigger data-testid="service-category-select"><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Sans catégorie —</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div><Label>Description courte</Label><Textarea rows={2} value={edit.short_description} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} /></div>

            <div>
              <Label>Description longue (éditeur riche)</Label>
              <RichEditor value={edit.long_description} onChange={(html) => setEdit({ ...edit, long_description: html })} minHeight={250} placeholder="Détails de la prestation, étapes, garanties…" />
            </div>

            {/* Image via Media Library */}
            <div>
              <Label>Image principale (depuis la médiathèque)</Label>
              <div className="flex items-start gap-3 mt-1.5">
                <div className="w-28 h-20 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] overflow-hidden flex items-center justify-center">
                  {edit.image_url ? <img src={edit.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Aucune</span>}
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={() => setShowMedia(true)} className="bg-[color:var(--brand-sky)]" data-testid="pick-from-media">
                      <LucideIcons.FolderOpen className="w-3.5 h-3.5 mr-1" />Choisir dans la médiathèque
                    </Button>
                    {edit.image_url && <Button type="button" size="sm" variant="outline" onClick={() => setEdit({ ...edit, image_url: "" })}>Retirer</Button>}
                  </div>
                  <Input value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} placeholder="Ou collez une URL directement" className="h-9 text-xs" />
                </div>
              </div>
            </div>

            {/* Icon picker */}
            <div>
              <Label>Icône affichée sur les cartes</Label>
              <IconPicker
                value={edit.icon}
                customUrl={edit.icon_url}
                onPick={(name) => setEdit({ ...edit, icon: name, icon_url: "" })}
                onUpload={(url) => setEdit({ ...edit, icon_url: url })}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>YouTube ID (optionnel)</Label><Input value={edit.youtube_url} onChange={(e) => setEdit({ ...edit, youtube_url: e.target.value })} placeholder="dQw4w9WgXcQ" /></div>
              <div><Label>Mot-clé SEO</Label><Input value={edit.keyword} onChange={(e) => setEdit({ ...edit, keyword: e.target.value })} /></div>
              <div><Label>Ordre</Label><Input type="number" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: parseInt(e.target.value) || 0 })} /></div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Meta title</Label><Input value={edit.meta_title} onChange={(e) => setEdit({ ...edit, meta_title: e.target.value })} /></div>
              <div><Label>Meta description</Label><Input value={edit.meta_description} onChange={(e) => setEdit({ ...edit, meta_description: e.target.value })} /></div>
            </div>

            <div className="flex items-center gap-2"><Switch checked={edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} id="feat" /><Label htmlFor="feat">Mettre en avant sur l'accueil</Label></div>

            <div className="flex gap-2 pt-2 border-t border-[hsl(var(--border))]">
              <Button variant="outline" onClick={() => setEdit(null)} className="flex-1">Annuler</Button>
              <Button onClick={save} className="flex-1 bg-[color:var(--brand-navy)] font-bold" data-testid="save-service-btn">
                <LucideIcons.Save className="w-4 h-4 mr-1.5" />Enregistrer
              </Button>
            </div>
          </Card>

          {showMedia && (
            <MediaPicker
              folder="services"
              onClose={() => setShowMedia(false)}
              onPick={(url) => { setEdit({ ...edit, image_url: url }); setShowMedia(false); }}
            />
          )}
        </div>
      )}
    </div>
  );
}
